import { compareNames, normalizeName } from '../shared/naming';
import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import { roundQuantity } from '../schedule/unit-of-measure';
import { TAKEOFF_ERROR, TAKEOFF_ERROR_MESSAGE } from './errors';
import type { CatalogItem, ServiceCatalog } from './service-catalog';
import { serviceElement, type AppliesTo } from './applies-to';
import type { MeasureBy } from './geometry';
import {
  foundationShares,
  foundationSteelRates,
  type FoundationFactors,
} from './foundation-factors';
import { ownFactorKey } from './own-factor';
import { pileRateKey } from './pile';
import { roomShares, type RoomFactors } from './room-quantity';
import { pileGaugeShares, pilePieceShares, pileTypeShares, type PileTotals } from './pile-totals';
import { gaugeShares, steelRatesFor, type SteelGauge } from './steel';
import { isPurchasable, type ItemKind } from './takeoff-line';

/**
 * O quantitativo virado do avesso: **item por item**, e não serviço por serviço.
 *
 * O catálogo é organizado como a obra pensa — disciplina, serviço, item —, e é assim que
 * alguém lança quantidade. Mas ninguém compra por serviço. O almoxarife precisa da outra
 * pergunta: *deste tijolo, quanto a obra inteira usa, e em que serviços?*
 *
 * As duas visões são a mesma informação, e é por isso que esta é **derivada** e não
 * guardada. Um total de item persistido ao lado dos lançamentos que o formam é um número
 * que envelhece sozinho — alguém corrige uma quantidade num serviço, e a lista de compra
 * continua dizendo o que dizia ontem.
 */

/** Três casas: é como se compra concreto, argamassa e tinta. */
const ITEM_DECIMALS = 3;

// ── A quantidade de cada item, em cada serviço ──────────────────────────────────

/** `"alvenaria|assentamento" + "E-685110"` — o lançamento de um item num serviço. */
export function itemQuantityKey(serviceKey: string, itemKey: string): string {
  return `${serviceKey}»${itemKey}`;
}

/**
 * O que a tipologia decidiu, e a que ela se compara.
 *
 * A ausência é o caso comum e significa **"segue a planilha"** — a mesma distinção que a
 * linha-sombra faz com o apto (RN-167). Guardar uma cópia de cada quantidade importada
 * transformaria "ninguém mexeu nisto" em "alguém confirmou isto", e as duas coisas se
 * decidem de formas diferentes quando a planilha for reimportada.
 */
export interface TakeoffQuantities {
  /** Por `itemQuantityKey`. Presente = alguém digitou; ausente = segue a planilha. */
  readonly overrides: Readonly<Record<string, number>>;
  /** A aba que orça esta tipologia. Ausente = não há planilha a seguir. */
  readonly sheetName?: string;
  /**
   * O medido de cada serviço nesta tipologia, pela chave do serviço (Fase 24, fatia 5).
   *
   * É a base do índice de consumo: o item calculado é este número vezes o índice. Vem de
   * fora — o traçado depende do desenho, que a consolidação não conhece — e ausente é o
   * serviço sem número, onde o índice não tem o que multiplicar.
   */
  readonly measured?: Readonly<Record<string, number>>;
  /** Por que a tipologia digitou por cima do calculado, por `itemQuantityKey` (D9). */
  readonly reasons?: Readonly<Record<string, string>>;
  /**
   * Quanto do aço é de cada bitola, pela chave do serviço (Fase 27, RN-378).
   *
   * É a taxa da disciplina do serviço virada em fração (`gaugeShares`): o item de aço do
   * CORTE E DOBRA reparte o medido do serviço por ela. É da obra, e não da tipologia — a
   * mesma em todas —, e vem de fora porque a taxa mora na disciplina, que o item não
   * conhece (`catalogGaugeShares`). Ausente, a consolidação a tira do próprio catálogo.
   */
  readonly gaugeShares?: Readonly<Record<string, Readonly<Partial<Record<SteelGauge, number>>>>>;
  /**
   * As estacas desta tipologia, para a participação do aço delas (Fase 27, etapa 1b): na
   * estaca, a bitola sai do tipo, e não da taxa da disciplina. Ausente = sem estaca.
   */
  readonly pile?: PileTotals;
  /**
   * A quantidade pronta por peça, somada na tipologia, por `itemQuantityKey` (Fase 28,
   * RN-384).
   *
   * Vem da planilha por peça ("B24 leva 33,18 kg de CA 50 - 8 MM"), que não é índice: o
   * número já é a quantidade, e o desenho não a recalcula. Soma-se ao calculado — o calculado
   * só mede as peças que **não** vieram da planilha (`semPecasImportadas`) — e perde para o
   * digitado, como tudo. Ausente = nenhuma peça importada trouxe este item.
   */
  readonly fromPieces?: Readonly<Record<string, number>>;
  /**
   * Quanto da medida de cada serviço da estaca é de cada tipo, pela chave do serviço e o id do
   * tipo (Fase 39, RN-508): a fração que leva o fator do tipo. Ausente, a consolidação a tira do
   * catálogo e de `pile`, como a das bitolas.
   */
  readonly pileShares?: Readonly<Record<string, Readonly<Record<string, number>>>>;
  /**
   * Quanto da medida de cada serviço da estaca é de cada estaca com fator próprio, pela chave do
   * serviço e o id da estaca (Fase 39, etapa 3, RN-511). A parte da estaca sai da parte do tipo
   * dela e leva o fator dela. Ausente, a consolidação a tira do catálogo e de `pile`.
   */
  readonly pilePieceShares?: Readonly<Record<string, Readonly<Record<string, number>>>>;
  /**
   * Os cômodos desta tipologia, com a medida de cada um e o fator próprio dos que têm (Fase 39,
   * etapa 3, RN-512). Ausente: nenhum cômodo tem fator próprio, e nada muda.
   */
  readonly roomFactors?: RoomFactors;
  /**
   * Quanto da medida de cada serviço que desce a cômodo é de cada cômodo, pela chave do serviço e
   * o id do cômodo (RN-512). Ausente, a consolidação a tira do catálogo e de `roomFactors`.
   */
  readonly roomShares?: Readonly<Record<string, Readonly<Record<string, number>>>>;
  /**
   * As peças da fundação desta tipologia — bloco, baldrame e radier —, com a medida de cada uma e
   * o fator que vale nas que têm (Fase 41, RN-539). Ausente: nenhuma peça tem fator, e nada muda.
   */
  readonly foundationFactors?: FoundationFactors;
  /**
   * Quanto da medida de cada serviço da fundação é de cada peça, pela chave do serviço e o id da
   * peça (RN-539). Ausente, a consolidação a tira do catálogo e de `foundationFactors`.
   */
  readonly foundationShares?: Readonly<Record<string, Readonly<Record<string, number>>>>;
}

/**
 * A participação de cada bitola, serviço a serviço — o que `TakeoffQuantities.gaugeShares`
 * espera (RN-378).
 *
 * Todo serviço de uma disciplina com taxa recebe a fração dela, e não só o CORTE E DOBRA:
 * é barato, e o item de aço que um dia chegar noutro nome de serviço continua sendo recusado
 * na gravação (RN-366), e não aqui. A disciplina sem taxa não entra — e o item de aço dela não
 * calcula.
 *
 * O serviço que mede a **estaca** é a exceção (Fase 27, RN-367): o aço dele vem do tipo de cada
 * estaca desenhada, e a proporção entre bitolas muda de um tipo para o outro, então a fração é a
 * dos tipos das estacas posicionadas (`pile`) — a taxa da disciplina não vale ali, nem como
 * reserva. Sem `pile` (ninguém passou as estacas) ou sem estaca com aço, o serviço fica sem
 * participação, e o item de aço não calcula: dividir pela taxa da disciplina seria mostrar uma
 * proporção que a estaca não tem. É por tipologia, e não da obra: quem consolida a obra a monta
 * com as estacas de cada uma.
 */
export function catalogGaugeShares(
  catalog: Pick<ServiceCatalog, 'disciplines'>,
  pile?: Pick<PileTotals, 'steelByGauge'>,
  fundacao?: Pick<FoundationFactors, 'pieces'>,
): Readonly<Record<string, Readonly<Partial<Record<SteelGauge, number>>>>> {
  const porServico: Record<string, Readonly<Partial<Record<SteelGauge, number>>>> = {};
  const daEstaca = pile === undefined ? {} : pileGaugeShares(pile);
  for (const disciplina of catalog.disciplines) {
    for (const servico of disciplina.services) {
      // A taxa que o serviço lê: a do elemento dele, na disciplina que mistura (Fase 42, RN-573).
      const daDisciplina = steelRatesFor(disciplina, serviceElement(servico));
      // Na fundação com taxa por peça (Fase 41, RN-546), a bitola reparte pela taxa equivalente
      // das peças desta tipologia; sem ela, pela da disciplina, como sempre.
      const daFundacao = foundationSteelRates(servico, fundacao, daDisciplina);
      const fracao =
        serviceElement(servico) === 'ESTACA'
          ? daEstaca
          : daFundacao === undefined
            ? gaugeShares(daDisciplina)
            : gaugeShares(daFundacao);
      if (Object.keys(fracao).length > 0) porServico[servico.key] = fracao;
    }
  }
  return porServico;
}

/**
 * A parte de cada tipo de estaca na medida de cada serviço que mede a estaca — o que
 * `TakeoffQuantities.pileShares` espera (Fase 39, RN-508). Só o serviço da estaca, e só quando
 * algum tipo mede.
 */
export function catalogPileShares(
  catalog: Pick<ServiceCatalog, 'disciplines'>,
  pile?: Pick<PileTotals, 'byType'>,
): Readonly<Record<string, Readonly<Record<string, number>>>> {
  const porServico: Record<string, Readonly<Record<string, number>>> = {};
  if (pile?.byType === undefined) return porServico;
  for (const disciplina of catalog.disciplines) {
    for (const servico of disciplina.services) {
      if (serviceElement(servico) !== 'ESTACA') continue;
      const partes = pileTypeShares(servico.measureBy, pile, servico.key);
      if (Object.keys(partes).length > 0) porServico[servico.key] = partes;
    }
  }
  return porServico;
}

/**
 * A parte de cada estaca com fator próprio na medida de cada serviço que mede a estaca — o que
 * `TakeoffQuantities.pilePieceShares` espera (Fase 39, etapa 3, RN-511). Só o serviço da estaca,
 * e só quando alguma estaca contada tem fator próprio.
 */
export function catalogPilePieceShares(
  catalog: Pick<ServiceCatalog, 'disciplines'>,
  pile?: Pick<PileTotals, 'byType' | 'byPiece'>,
): Readonly<Record<string, Readonly<Record<string, number>>>> {
  const porServico: Record<string, Readonly<Record<string, number>>> = {};
  if (pile?.byPiece === undefined) return porServico;
  for (const disciplina of catalog.disciplines) {
    for (const servico of disciplina.services) {
      if (serviceElement(servico) !== 'ESTACA') continue;
      const partes = pilePieceShares(servico.measureBy, pile, servico.key);
      if (Object.keys(partes).length > 0) porServico[servico.key] = partes;
    }
  }
  return porServico;
}

/**
 * A parte de cada cômodo na medida de cada serviço que desce a cômodo — o que
 * `TakeoffQuantities.roomShares` espera (Fase 39, etapa 3, RN-512). Só quando algum cômodo tem
 * fator próprio: sem ele, a conta não precisa ver cômodo nenhum.
 */
export function catalogRoomShares(
  catalog: Pick<ServiceCatalog, 'disciplines'>,
  rooms?: RoomFactors,
): Readonly<Record<string, Readonly<Record<string, number>>>> {
  const porServico: Record<string, Readonly<Record<string, number>>> = {};
  if (rooms === undefined) return porServico;
  for (const disciplina of catalog.disciplines) {
    for (const servico of disciplina.services) {
      const partes = roomShares(servico, rooms);
      if (Object.keys(partes).length > 0) porServico[servico.key] = partes;
    }
  }
  return porServico;
}

/**
 * A parte de cada peça da fundação na medida de cada serviço que mede bloco, baldrame ou radier —
 * o que `TakeoffQuantities.foundationShares` espera (Fase 41, RN-539). Só quando alguma peça tem
 * fator: sem ele, a conta não precisa ver peça nenhuma. No serviço de aço, a parte é o aço da peça,
 * com a taxa da disciplina na peça sem a dela (RN-556).
 */
export function catalogFoundationShares(
  catalog: Pick<ServiceCatalog, 'disciplines'>,
  base?: Pick<FoundationFactors, 'pieces'>,
): Readonly<Record<string, Readonly<Record<string, number>>>> {
  const porServico: Record<string, Readonly<Record<string, number>>> = {};
  if (base === undefined) return porServico;
  for (const disciplina of catalog.disciplines) {
    for (const servico of disciplina.services) {
      const partes = foundationShares(
        servico,
        base,
        steelRatesFor(disciplina, serviceElement(servico)),
      );
      if (Object.keys(partes).length > 0) porServico[servico.key] = partes;
    }
  }
  return porServico;
}

/**
 * O item calculado cômodo a cômodo (Fase 39, RN-512): Σ medida × parte do cômodo × (fator do
 * cômodo, ou o índice da obra) × (1 + perda).
 *
 * Só quando algum cômodo tem fator próprio para **este** item — senão devolve `undefined` e quem
 * chama faz a conta de sempre, medida × índice. O cômodo sem fator próprio e o item sem índice
 * não somam; sem nenhum cômodo que some, o item não calcula.
 */
export function calculatedByRoom(
  item: Pick<CatalogItem, 'key' | 'kind' | 'consumptionIndex' | 'lossPercent'>,
  serviceKey: string,
  measured: number | undefined,
  shares: Readonly<Record<string, number>>,
  factors: RoomFactors['factors'] | undefined,
): number | undefined {
  if (measured === undefined) return undefined;
  const perda = item.kind === 'MAO_DE_OBRA' ? 0 : (item.lossPercent ?? 0);
  const chave = ownFactorKey(serviceKey, item.key);
  const daObra =
    item.consumptionIndex !== undefined && item.consumptionIndex > 0
      ? item.consumptionIndex
      : undefined;
  let soma = 0;
  let algum = false;
  for (const [comodo, parte] of Object.entries(shares)) {
    const fator = factors?.[comodo]?.[chave] ?? daObra;
    if (fator === undefined || fator <= 0 || parte <= 0) continue;
    soma += measured * parte * fator;
    algum = true;
  }
  return algum ? soma * (1 + perda / 100) : undefined;
}

/** Algum cômodo com parte neste serviço tem fator próprio para o item? */
function temFatorDoComodo(
  shares: Readonly<Record<string, number>> | undefined,
  factors: RoomFactors['factors'] | undefined,
  chave: string,
): boolean {
  if (shares === undefined || factors === undefined) return false;
  return Object.keys(shares).some((comodo) => factors[comodo]?.[chave] !== undefined);
}

/**
 * Abaixo disto, o que sobrou de um tipo depois de tirar as estacas com fator próprio é o resto da
 * divisão em ponto flutuante, e não estaca: com todas as R40 de fator próprio, o R40 não soma.
 */
const SOBRA_DO_TIPO = 1e-12;

/**
 * O item da estaca calculado parte a parte (Fase 39, RN-508, RN-511): Σ medida × parte × fator ×
 * (1 + perda).
 *
 * As partes são grupos de estacas, e cada grupo leva um fator:
 *
 * - **a estaca com fator próprio para o item** (`pieceShares`) é um grupo só dela, com o fator
 *   dela — por cima do fator do tipo (D14);
 * - **o resto de cada tipo** é outro grupo: a parte do tipo menos a das estacas dele que têm
 *   fator próprio, com o fator do tipo.
 *
 * O tipo sem fator para o item usa o índice da obra (RN-280), que é o número que a obra já tinha —
 * nunca o fator de outro diâmetro (D6). O grupo sem nenhum dos dois não soma nada; sem nenhum
 * grupo que some, o item não calcula, como o item sem índice. A estaca com fator próprio para
 * outro item segue o tipo neste. A medida corrigida na tipologia reparte na mesma proporção.
 */
export function calculatedByPileType(
  item: Pick<CatalogItem, 'key' | 'kind' | 'consumptionIndex' | 'lossPercent'>,
  serviceKey: string,
  measured: number | undefined,
  shares: Readonly<Record<string, number>>,
  pile: Pick<PileTotals, 'byType' | 'byPiece'> | undefined,
  pieceShares?: Readonly<Record<string, number>>,
): number | undefined {
  if (measured === undefined) return undefined;
  const perda = item.kind === 'MAO_DE_OBRA' ? 0 : (item.lossPercent ?? 0);
  const chave = pileRateKey(serviceKey, item.key);
  const daObra =
    item.consumptionIndex !== undefined && item.consumptionIndex > 0
      ? item.consumptionIndex
      : undefined;
  let soma = 0;
  let algum = false;

  // As estacas com fator próprio para este item, cada uma com o dela; a parte delas sai do tipo.
  const doTipo: Record<string, number> = { ...shares };
  for (const [peca, parte] of Object.entries(pieceShares ?? {})) {
    const daPeca = pile?.byPiece?.[peca];
    const propria = daPeca?.factors[ownFactorKey(serviceKey, item.key)];
    if (daPeca === undefined || propria === undefined || propria <= 0 || parte <= 0) continue;
    soma += measured * parte * propria;
    algum = true;
    doTipo[daPeca.pileTypeId] = (doTipo[daPeca.pileTypeId] ?? 0) - parte;
  }

  for (const [tipo, parte] of Object.entries(doTipo)) {
    const fator = pile?.byType?.[tipo]?.rates[chave] ?? daObra;
    if (fator === undefined || fator <= 0 || parte <= SOBRA_DO_TIPO) continue;
    soma += measured * parte * fator;
    algum = true;
  }
  return algum ? soma * (1 + perda / 100) : undefined;
}

/**
 * De onde veio a quantidade do item (Fase 24, D9) — a tela diz qual das três é.
 *
 * `MANUAL` é o digitado na tipologia; `CALCULADO`, o medido vezes o índice; `PLANILHA`, a
 * aba que orça a tipologia — também quando a aba não trouxe nada, e aí a quantidade é
 * `undefined`. `PECAS` é a planilha por peça (Fase 28), somada ao calculado quando houver.
 */
export type QuantityOrigin = 'MANUAL' | 'PECAS' | 'CALCULADO' | 'PLANILHA';

export interface ResolvedQuantity {
  /** `undefined` = a planilha não trouxe, o índice não calcula e ninguém digitou. Não é zero. */
  readonly quantity: number | undefined;
  readonly origin: QuantityOrigin;
  /** O que a planilha dizia — a tela mostra o de→para quando alguém discordou. */
  readonly fromSheet: number | undefined;
  /**
   * O que o índice calcula — ao lado do digitado, quando alguém discordou dele (D9).
   * `undefined` = o item não tem índice, ou o serviço não tem medido.
   */
  readonly calculated: number | undefined;
  /** A soma das peças importadas (Fase 28) — ao lado do digitado, quando alguém o trocou. */
  readonly fromPieces?: number;
  /** O motivo de quem digitou por cima do calculado. */
  readonly reason?: string;
}

/**
 * O item calculado: medido × índice × (1 + perda) — **sem arredondamento** (D8).
 *
 * O item de **aço** troca o índice pela participação da bitola (Fase 27, RN-378): medido ×
 * participação × (1 + perda). O medido do CORTE E DOBRA já é o kg da peça — volume × taxa da
 * disciplina —, e a participação diz quanto desse kg é de 6,3, de 10, de 16. Com o medido
 * corrigido ou digitado na tipologia, a divisão continua sendo a da taxa: quem corrige o kg
 * total não está dizendo que mudou a armadura. Sem participação para a bitola (a disciplina
 * não tem taxa, ou não tem a desta bitola), o item não calcula.
 *
 * Arredondar aqui seria arredondar cada tipologia, e a soma de vinte arredondamentos não é o
 * arredondamento da soma: 51,156 sacos em vinte andares são 1.023,12, e não 1.040. A
 * embalagem é decidida no pedido, sobre o total consolidado.
 *
 * - **Índice zero é "não preenchido"**, e não "consome nada" (D14): é como o índice da mão
 *   de obra nasce enquanto a sugestão de produtividade não chega (fatia 7). Com zero valendo
 *   conta, cada item novo apagaria a quantidade da planilha com um zero calculado. O item
 *   que não acontece na tipologia se diz zerando a quantidade dela (RN-253).
 * - **Mão de obra não tem perda** (D8): hora de gente não se perde no caminho.
 */
export function calculatedItemQuantity(
  item: Pick<CatalogItem, 'kind' | 'consumptionIndex' | 'lossPercent' | 'steelGauge'>,
  measured: number | undefined,
  shares?: Readonly<Partial<Record<SteelGauge, number>>>,
): number | undefined {
  const perda = item.kind === 'MAO_DE_OBRA' ? 0 : (item.lossPercent ?? 0);
  if (item.steelGauge !== undefined) {
    const participacao = shares?.[item.steelGauge];
    if (measured === undefined || participacao === undefined || participacao <= 0) {
      return undefined;
    }
    return measured * participacao * (1 + perda / 100);
  }
  const indice = item.consumptionIndex;
  if (measured === undefined || indice === undefined || indice <= 0) return undefined;
  return measured * indice * (1 + perda / 100);
}

/**
 * A quantidade do item nesta tipologia — **digitada > calculada > planilha > nada** (D9).
 *
 * O digitado vence porque é decisão de gente sobre esta tipologia; o calculado vence a
 * planilha porque sai do desenho desta obra, e a planilha é o orçamento de outra conta. O
 * calculado continua na resposta quando alguém digitou por cima: a tela o mostra ao lado,
 * com o motivo.
 */
export function resolveItemQuantity(
  item: CatalogItem,
  serviceKey: string,
  quantities: TakeoffQuantities,
): ResolvedQuantity {
  const daPlanilha =
    quantities.sheetName === undefined ? undefined : item.quantityBySheet[quantities.sheetName];
  const chave = itemQuantityKey(serviceKey, item.key);
  const digitada = quantities.overrides[chave];
  // Na estaca, o item que não é aço calcula tipo a tipo, cada diâmetro com o fator dele (Fase 39,
  // RN-508), e a estaca com fator próprio com o dela (RN-511). O aço continua pela participação
  // das bitolas dos tipos (RN-378).
  const porTipo = item.steelGauge === undefined ? quantities.pileShares?.[serviceKey] : undefined;
  // No serviço que desce a cômodo, o cômodo com fator próprio para o item leva o dele, e os
  // outros o índice da obra (Fase 39, RN-512). Sem cômodo com fator próprio, a conta de sempre.
  const porComodo =
    item.steelGauge === undefined &&
    temFatorDoComodo(
      quantities.roomShares?.[serviceKey],
      quantities.roomFactors?.factors,
      ownFactorKey(serviceKey, item.key),
    )
      ? quantities.roomShares?.[serviceKey]
      : undefined;
  // Na fundação, a peça com fator para o item — o próprio, ou o do tipo de bloco — leva o dela, e
  // as outras o índice da obra (Fase 41, RN-539). É a mesma conta do cômodo, peça a peça; no
  // serviço de aço, a parte de cada peça é o aço dela (RN-556).
  const porPeca =
    item.steelGauge === undefined &&
    temFatorDoComodo(
      quantities.foundationShares?.[serviceKey],
      quantities.foundationFactors?.factors,
      ownFactorKey(serviceKey, item.key),
    )
      ? quantities.foundationShares?.[serviceKey]
      : undefined;
  const calculada =
    porTipo !== undefined
      ? calculatedByPileType(
          item,
          serviceKey,
          quantities.measured?.[serviceKey],
          porTipo,
          quantities.pile,
          quantities.pilePieceShares?.[serviceKey],
        )
      : porComodo !== undefined
        ? calculatedByRoom(
            item,
            serviceKey,
            quantities.measured?.[serviceKey],
            porComodo,
            quantities.roomFactors?.factors,
          )
        : porPeca !== undefined
          ? calculatedByRoom(
              item,
              serviceKey,
              quantities.measured?.[serviceKey],
              porPeca,
              quantities.foundationFactors?.factors,
            )
          : calculatedItemQuantity(
              item,
              quantities.measured?.[serviceKey],
              quantities.gaugeShares?.[serviceKey],
            );
  const dasPecas = quantities.fromPieces?.[chave];
  const comPecas = dasPecas === undefined ? {} : { fromPieces: dasPecas };

  if (digitada !== undefined) {
    const motivo = quantities.reasons?.[chave];
    return {
      quantity: digitada,
      origin: 'MANUAL',
      fromSheet: daPlanilha,
      calculated: calculada,
      ...comPecas,
      ...(motivo === undefined ? {} : { reason: motivo }),
    };
  }
  // A peça importada traz o número pronto; o calculado, quando há, é o das outras peças da
  // tipologia — as desenhadas sem planilha —, e as duas somam (RN-384).
  if (dasPecas !== undefined) {
    return {
      quantity: dasPecas + (calculada ?? 0),
      origin: 'PECAS',
      fromSheet: daPlanilha,
      calculated: calculada,
      fromPieces: dasPecas,
    };
  }
  if (calculada !== undefined) {
    return {
      quantity: calculada,
      origin: 'CALCULADO',
      fromSheet: daPlanilha,
      calculated: calculada,
    };
  }
  return { quantity: daPlanilha, origin: 'PLANILHA', fromSheet: daPlanilha, calculated: undefined };
}

/**
 * Digitar este número precisa de motivo? (D9)
 *
 * Só quando há calculado: digitar por cima dele é discordar da conta. No item sem índice, o
 * digitado continua sendo o que era antes desta fase — a quantidade, sem discordância.
 */
export function overrideNeedsReason(calculated: number | undefined): boolean {
  return calculated !== undefined;
}

/**
 * O número digitado por cima do calculado, conferido (D9).
 *
 * O motivo que vier sem calculado não é guardado: ele explicaria uma discordância que não
 * existe, e confundiria quem lesse a decisão depois.
 */
export function decideItemOverride(input: {
  readonly description: string;
  readonly calculated: number | undefined;
  readonly reason: string | undefined;
}): Result<{ readonly reason?: string }, DomainError> {
  if (!overrideNeedsReason(input.calculated)) return ok({});
  const motivo = input.reason?.trim() ?? '';
  if (motivo === '') {
    return err(
      domainError(
        TAKEOFF_ERROR.ITEM_OVERRIDE_REASON_REQUIRED,
        `${TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.ITEM_OVERRIDE_REASON_REQUIRED]!} Item: ${input.description}.`,
        { description: input.description },
      ),
    );
  }
  return ok({ reason: motivo });
}

// ── A consolidação ──────────────────────────────────────────────────────────────

/**
 * Quem identifica o material **entre serviços** é o código de compras.
 *
 * Dentro de um serviço, a chave do item resolve pela descrição quando não há código
 * (`resolveItemKey`). Entre serviços isso não basta: o mesmo saco de argamassa aparece
 * como "PRÉ MASSA" na alvenaria e "PRE-MASSA CINZA" no reboco, com o mesmo `E-182844`, e
 * somá-los em duas linhas separadas daria à obra duas compras onde há uma. O código é a
 * identidade do material no catálogo (RN-011); a descrição é só como alguém o escreveu.
 *
 * Sem código, soma pela **descrição normalizada** (RN-250) — é o item "sem código de
 * compras", e ele continua marcado assim até alguém vincular. O código é comparado sem
 * diferenciar maiúsculas, como o cadastro de materiais o compara: `e-685110` e `E-685110`
 * são o mesmo tijolo.
 *
 * A mão de obra **não** soma pelo código — hora de gente não se compra (RN-166). Soma pela
 * função quando o item aponta uma (RN-252), e pela descrição quando ainda não: é a mesma
 * hora de pedreiro na alvenaria e no reboco.
 *
 * O prefixo separa os três espaços: sem ele, um material descrito "e-685110" cairia na
 * linha do código `E-685110`.
 */
export function consolidationKey(item: CatalogItem): string {
  if (item.kind === 'MAO_DE_OBRA') {
    return item.jobRoleId === undefined
      ? `descricao:${normalizeName(item.description)}`
      : `funcao:${item.jobRoleId}`;
  }
  return item.code === undefined
    ? `descricao:${normalizeName(item.description)}`
    : `codigo:${normalizeName(item.code)}`;
}

/** Quanto um serviço consome de um item. */
export interface ItemUsage {
  readonly serviceKey: string;
  readonly serviceName: string;
  readonly disciplineName: string;
  /** Como o serviço mede, e de que peça — a exportação escreve "volume de pilar" (Fase 24). */
  readonly measureBy: MeasureBy;
  readonly appliesTo?: AppliesTo;
  /** A margem da soleira — a exportação escreve "largura + margem de porta" (RN-310). */
  readonly sideMargin?: number;
  readonly quantity: number;
}

export interface ConsolidatedItem {
  readonly key: string;
  readonly code?: string;
  readonly description: string;
  readonly unit?: string;
  /**
   * O que ele é — e, sobretudo, se ele vira pedido de compra.
   *
   * Mão de obra some daqui se for tratada como material: hora de pedreiro entraria na
   * lista que o almoxarife manda para o fornecedor, misturada com tijolo. Ela sai em
   * homem-hora e vai para o planejamento, não para a compra (RN-166).
   */
  readonly kind: ItemKind;
  /** Um por serviço que consome este item, na ordem do catálogo. */
  readonly usages: readonly ItemUsage[];
  /** A soma dos usos — o número que vai para a compra. */
  readonly total: number;
}

interface EmMontagem {
  key: string;
  code?: string;
  description: string;
  unit?: string;
  kind: ItemKind;
  /** Por `serviceKey`, para que dois lançamentos do mesmo serviço somem numa linha só. */
  readonly usos: Map<string, ItemUsage>;
  total: number;
}

/**
 * Acumula um catálogo inteiro no mapa, multiplicando por quantos pavimentos o repetem.
 *
 * O multiplicador é o que separa "o desenho" de "o prédio": a tipologia é lançada uma vez
 * e acontece vinte (RN-167). Somar sem ele daria a compra de um andar.
 */
function acumular(
  destino: Map<string, EmMontagem>,
  catalog: ServiceCatalog,
  quantidades: TakeoffQuantities,
  vezes: number,
): void {
  // A participação das bitolas é do catálogo (a taxa mora na disciplina): quem não a trouxe
  // pronta não fica sem o aço na lista de compra por isso (RN-378).
  // A parte de cada tipo de estaca também (Fase 39): sem ela, o item da estaca calcularia pelo
  // índice da obra e não pelo fator de cada diâmetro.
  const quantities: TakeoffQuantities = {
    ...quantidades,
    ...(quantidades.gaugeShares === undefined
      ? {
          gaugeShares: catalogGaugeShares(catalog, quantidades.pile, quantidades.foundationFactors),
        }
      : {}),
    ...(quantidades.pileShares === undefined
      ? { pileShares: catalogPileShares(catalog, quantidades.pile) }
      : {}),
    // E a de cada estaca com fator próprio (Fase 39, etapa 3, RN-511), pelo mesmo motivo.
    ...(quantidades.pilePieceShares === undefined
      ? { pilePieceShares: catalogPilePieceShares(catalog, quantidades.pile) }
      : {}),
    // E a de cada cômodo, quando algum tem fator próprio (Fase 39, etapa 3, RN-512).
    ...(quantidades.roomShares === undefined
      ? { roomShares: catalogRoomShares(catalog, quantidades.roomFactors) }
      : {}),
    // E a de cada peça da fundação, quando alguma tem fator (Fase 41, RN-539).
    ...(quantidades.foundationShares === undefined
      ? { foundationShares: catalogFoundationShares(catalog, quantidades.foundationFactors) }
      : {}),
  };
  for (const disciplina of catalog.disciplines) {
    for (const servico of disciplina.services) {
      for (const item of servico.items) {
        const { quantity } = resolveItemQuantity(item, servico.key, quantities);
        if (quantity === undefined || quantity === 0) continue;

        const usado = roundQuantity(quantity * vezes, ITEM_DECIMALS);
        const chave = consolidationKey(item);

        let alvo = destino.get(chave);
        if (alvo === undefined) {
          alvo = {
            key: chave,
            description: item.description,
            kind: item.kind,
            usos: new Map(),
            total: 0,
            // Mão de obra não carrega código de compras para a lista: é o código que faz
            // uma linha parecer comprável, e hora de gente não é (RN-166).
            ...(item.code === undefined || item.kind === 'MAO_DE_OBRA' ? {} : { code: item.code }),
            ...(item.unit === undefined ? {} : { unit: item.unit }),
          };
          destino.set(chave, alvo);
        }
        // A unidade que faltava numa aba pode vir de outra; a que já existe manda.
        if (alvo.unit === undefined && item.unit !== undefined) alvo.unit = item.unit;

        const anterior = alvo.usos.get(servico.key);
        alvo.usos.set(servico.key, {
          serviceKey: servico.key,
          serviceName: servico.name,
          disciplineName: disciplina.name,
          measureBy: servico.measureBy,
          ...(servico.appliesTo === undefined ? {} : { appliesTo: servico.appliesTo }),
          ...(servico.sideMargin === undefined ? {} : { sideMargin: servico.sideMargin }),
          quantity: roundQuantity((anterior?.quantity ?? 0) + usado, ITEM_DECIMALS),
        });
        alvo.total = roundQuantity(alvo.total + usado, ITEM_DECIMALS);
      }
    }
  }
}

/**
 * Ordem alfabética pela descrição.
 *
 * Não é a ordem do catálogo de propósito: quem lê esta lista está procurando **um
 * material**, e não percorrendo a obra. Ordenar por código deixaria de fora justamente os
 * itens que ainda não têm um.
 */
function ordenar(itens: Iterable<EmMontagem>): readonly ConsolidatedItem[] {
  return [...itens]
    .map((item): ConsolidatedItem => ({
      key: item.key,
      description: item.description,
      kind: item.kind,
      usages: [...item.usos.values()],
      total: item.total,
      ...(item.code === undefined ? {} : { code: item.code }),
      ...(item.unit === undefined ? {} : { unit: item.unit }),
    }))
    .sort((a, b) => compareNames(a.description, b.description));
}

/** O quantitativo de **uma** tipologia, item a item. */
export function consolidateItems(
  catalog: ServiceCatalog,
  quantities: TakeoffQuantities,
): readonly ConsolidatedItem[] {
  const mapa = new Map<string, EmMontagem>();
  acumular(mapa, catalog, quantities, 1);
  return ordenar(mapa.values());
}

/** Uma tipologia da obra: o que ela decidiu e quantos pavimentos a repetem. */
export interface TypologyTakeoff {
  readonly name: string;
  readonly floors: number;
  readonly quantities: TakeoffQuantities;
  /**
   * Os serviços **desta** tipologia — o recorte do que ela escolheu usar.
   *
   * Ausente, vale o catálogo da obra inteiro. Presente, é ele que se soma: a cobertura
   * não compra a impermeabilização que só o apartamento tipo escolheu, ainda que as duas
   * tenham quantidade guardada para ela.
   */
  readonly catalog?: ServiceCatalog;
}

/**
 * O quantitativo da **obra**: todas as tipologias, cada uma vezes os pavimentos dela.
 *
 * É este o número que vira pedido de compra, e é por isso que ele soma tipologias
 * diferentes na mesma linha: o tijolo é o mesmo tijolo, venha do apartamento tipo 1 ou da
 * circulação.
 */
export function consolidateProject(
  catalog: ServiceCatalog,
  typologies: readonly TypologyTakeoff[],
): readonly ConsolidatedItem[] {
  const mapa = new Map<string, EmMontagem>();
  for (const tipologia of typologies) {
    acumular(mapa, tipologia.catalog ?? catalog, tipologia.quantities, tipologia.floors);
  }
  return ordenar(mapa.values());
}

// ── Leitura ─────────────────────────────────────────────────────────────────────

export interface ConsolidationSummary {
  readonly items: number;
  /** Quantos têm código de compras — os que já sabem virar pedido. */
  readonly withCode: number;
  /** Quantos são consumidos por mais de um serviço — os que a soma existe para pegar. */
  readonly shared: number;
}

export function summarizeConsolidation(itens: readonly ConsolidatedItem[]): ConsolidationSummary {
  return {
    items: itens.length,
    withCode: itens.filter((item) => item.code !== undefined).length,
    shared: itens.filter((item) => item.usages.length > 1).length,
  };
}

/**
 * Separa o que se compra do que se contrata.
 *
 * As duas listas respondem a perguntas de pessoas diferentes: a de material vira pedido
 * de compra e é do almoxarife; a de mão de obra sai em homem-hora e é do planejamento.
 * Somá-las na mesma tabela poria hora de pedreiro num pedido de fornecedor.
 */
export function splitByPurchase(itens: readonly ConsolidatedItem[]): {
  readonly materials: readonly ConsolidatedItem[];
  readonly labor: readonly ConsolidatedItem[];
} {
  return {
    materials: itens.filter((item) => isPurchasable(item.kind)),
    labor: itens.filter((item) => !isPurchasable(item.kind)),
  };
}
